import { gql } from '@apollo/client';

/** The viewing brand's public settings (no secrets). */
export const CURRENT_BRAND = gql`
  query CurrentBrand {
    currentBrand {
      id
      brandSite
      displayName
      showOtherSites
      supportEmail
      homeBannerImageKey
      stripePublishableKey
    }
  }
`;

const BRAND_SETTINGS_FIELDS = `
  id
  brandSite
  displayName
  aliases
  referralCommissionRate
  showOtherSites
  banBrandSites
  banAllOtherBrands
  banMyselfOnOthers
  hideFromAllOtherBrands
  exclusiveMarkets {
    latitude
    longitude
    label
    radiusMiles
  }
  fromEmail
  supportEmail
  homeBannerImageKey
  homePage {
    webTitle
    webSubtitle
    appTitle
    appSubtitle
    overlayColor
    overlayOpacity
  }
  stripePublishableKey
  defaultProductType
  allowedProductTypes
  createdAt
  updatedAt
`;

/**
 * Spec 029: the home page banner, overlay and words. A query of its own, so an
 * older API without `homePage` never breaks `CURRENT_BRAND` (Stripe key).
 */
export const HOME_PAGE_SETTINGS = gql`
  query HomePageSettings {
    currentBrand {
      id
      brandSite
      homeBannerImageKey
      homePage {
        webTitle
        webSubtitle
        appTitle
        appSubtitle
        overlayColor
        overlayOpacity
      }
    }
  }
`;

export const MY_BRAND = gql`
  query MyBrand {
    myBrand {
      ${BRAND_SETTINGS_FIELDS}
    }
  }
`;

export const GET_PRESIGNED_URL_FOR_BRAND_BANNER = gql`
  mutation GetPresignedUrlForBrandBanner($fileType: String!, $contentLength: Int!) {
    getPresignedUrlForBrandBanner(fileType: $fileType, contentLength: $contentLength) {
      url
      key
    }
  }
`;

export const UPDATE_BRAND_SETTINGS = gql`
  mutation UpdateBrandSettings($input: UpdateBrandSettingsInput!) {
    updateBrandSettings(input: $input) {
      ${BRAND_SETTINGS_FIELDS}
    }
  }
`;

export const BRAND_HOSTS = gql`
  query BrandHosts {
    brandHosts {
      id
      userId
      brandId
      isAdmin
      hostStatus
      listingsSuspended
      approvedAt
      openTripCount
      user {
        id
        shortId
        email
        firstName
        lastName
        displayName
      }
    }
  }
`;

/** D41: a host's booked trips on this brand that have not ended, soonest first (brand admin). */
export const HOST_OPEN_BOOKINGS_ON_BRAND = gql`
  query HostOpenBookingsOnBrand($userId: ID!) {
    hostOpenBookingsOnBrand(userId: $userId) {
      id
      status
      startAt
      endAt
      brandSite
      hostFinishedCheckin
      guestFinishedCheckin
      hostFinishedCheckout
      guest {
        id
        firstName
        lastName
        email
      }
      car {
        id
        name
        year
        make
        model
        location {
          id
          timezone
        }
      }
    }
  }
`;

/** D41: cancels a future trip of a paused host with a full refund; both sides are told. */
export const ADMIN_CANCEL_BOOKING_FOR_PAUSED_HOST = gql`
  mutation AdminCancelBookingForPausedHost($bookingId: ID!, $note: String) {
    adminCancelBookingForPausedHost(bookingId: $bookingId, note: $note) {
      id
      status
      cancellationReason
    }
  }
`;

export const SUSPEND_HOST_LISTINGS = gql`
  mutation SuspendHostListings($userId: String!) {
    suspendHostListings(userId: $userId) {
      id
    }
  }
`;

export const RESUME_HOST_LISTINGS = gql`
  mutation ResumeHostListings($userId: String!) {
    resumeHostListings(userId: $userId) {
      id
    }
  }
`;

export const REVOKE_HOST_APPROVAL = gql`
  mutation RevokeHostApproval($userId: String!) {
    revokeHostApproval(userId: $userId) {
      id
    }
  }
`;

// ─── platform owner ───────────────────────────────────────────────────────────

export const BRANDS = gql`
  query Brands {
    brands {
      ${BRAND_SETTINGS_FIELDS}
    }
  }
`;

export const CREATE_BRAND = gql`
  mutation CreateBrand($input: CreateBrandInput!) {
    createBrand(input: $input) {
      ${BRAND_SETTINGS_FIELDS}
    }
  }
`;

export const UPDATE_BRAND_ALIASES = gql`
  mutation UpdateBrandAliases($input: UpdateBrandAliasesInput!) {
    updateBrandAliases(input: $input) {
      id
      brandSite
      aliases
    }
  }
`;

/** Platform owner (spec 016): what a brand sells, cars or places to stay. */
export const UPDATE_BRAND_PRODUCT_TYPES = gql`
  mutation UpdateBrandProductTypes($input: UpdateBrandProductTypesInput!) {
    updateBrandProductTypes(input: $input) {
      id
      brandSite
      defaultProductType
      allowedProductTypes
    }
  }
`;

/** The stays service fee percents (spec 016 §4.4); everyone may read them. */
export const STAY_SERVICE_FEE_RATES = gql`
  query StayServiceFeeRatesAdmin {
    stayServiceFeeRates {
      guestServiceFeeRate
      hostServiceFeeRate
    }
  }
`;

export const UPDATE_STAY_SERVICE_FEE_RATES = gql`
  mutation UpdateStayServiceFeeRates($input: UpdateStayServiceFeeRatesInput!) {
    updateStayServiceFeeRates(input: $input) {
      guestServiceFeeRate
      hostServiceFeeRate
    }
  }
`;

export const UPDATE_BRAND_STRIPE_SECRETS = gql`
  mutation UpdateBrandStripeSecrets($input: UpdateBrandStripeSecretsInput!) {
    updateBrandStripeSecrets(input: $input) {
      id
      brandSite
      stripePublishableKey
    }
  }
`;

export const SET_BRAND_ADMIN = gql`
  mutation SetBrandAdmin($input: SetBrandAdminInput!) {
    setBrandAdmin(input: $input) {
      id
      userId
      brandId
      isAdmin
      hostStatus
    }
  }
`;

export const PLATFORM_SETTINGS = gql`
  query PlatformSettings {
    platformSettings {
      id
      defaultReferralCommissionRate
      minimumReferralCommissionRate
      updatedAt
    }
  }
`;

export const UPDATE_PLATFORM_REFERRAL_RATES = gql`
  mutation UpdatePlatformReferralRates($input: UpdatePlatformReferralRatesInput!) {
    updatePlatformReferralRates(input: $input) {
      id
      defaultReferralCommissionRate
      minimumReferralCommissionRate
      updatedAt
    }
  }
`;
